
import { css } from '@emotion/core';
import {
  useState, useEffect, useContext, useRef,
} from 'react';
import { standardSpacing, largeSpacing } from 'styles/global_defaults/scaffolding';
import { gray5 } from 'styles/global_defaults/colors';
import { connect } from 'react-redux';
import { RootState } from 'redux/schemas';
import moment from 'moment';
import { getPeriodStartDate, getPeriodEndDate, noContractPeriodDefined } from 'redux/selectors/licenses';
import t from 'react-translate';
import { desktop, notDesktop } from 'styles/global_defaults/media-queries';
import { EnrollmentPeriod } from 'redux/schemas/models/license';
import { AngularServicesContext } from 'react-app';
import { embedAngularTemplate } from 'shared/embed-angular';
import { Route, useRouteMatch } from 'react-router-dom';
import NvRouterSwitch from 'nv-router/nv-switch';
import TopEnrolledPanel from './top-enrolled-panel';
import EnrollmentPanel, { defaultPeriod, defaultPeriodType } from './enrollment-panel';
import LicenseUsagePanel from './license-usage-panel';

type LicenseDashboardProps = {
  licensePeriodDefined: boolean,
  periodStartDate: moment.Moment,
  periodEndDate: moment.Moment,
};

const LicenseDashboardComponent = (props: LicenseDashboardProps) => {
  /* For no reason I can discover, this page clips the bottom of the ranking bars on
  mobile Safair by overcalculating the size of 100vh. This is a bad hack to detect that
  browser and reduce the size of the page. Please do not copy elsewhere without good reason */
  // Note that this also breaks the UI when setting dev tools to do a responsive display as an iPhone
  const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const styles = css`
    ${isMobileSafari ? css`
      height: calc(100vh - 240px);
    ` : css`
      height: calc(100vh - 120px);
    `};

    width: 100%;

    & > * {
      ${desktop(css`
        padding-top: ${largeSpacing}px;
        padding-bottom: ${largeSpacing}px;
        padding-right: ${standardSpacing * 2}px;
        padding-left: ${standardSpacing * 2}px;
      `)};

      ${notDesktop(css`
        padding-top: ${standardSpacing}px;
        padding-bottom: ${standardSpacing}px;
        padding-right: ${standardSpacing}px;
        padding-left: ${standardSpacing}px;
      `)};
    }

    padding-bottom: ${largeSpacing * 2}px;

    overflow: scroll;
  `;

  const [startDate, setStartDate] = useState(defaultPeriod[0]);
  const [endDate, setEndDate] = useState(defaultPeriod[1]);
  const [periodText, setPeriodText] = useState(t.INSTITUTIONS.LICENSES.DASHBOARD.CURRENT_CONTRACT_PERIOD());
  const [periodType, setPeriodType] = useState(defaultPeriodType);

  // A callback for updating the period used in the EnrollmentPanel since that's where
  // the current date period is updated
  const setPeriod = (newPeriodStart: moment.Moment, newPeriodEnd: moment.Moment, newPeriodType: EnrollmentPeriod, text: string) => {
    setStartDate(newPeriodStart);
    setEndDate(newPeriodEnd);
    setPeriodText(text);
    setPeriodType(newPeriodType);
  };

  const Border = () => (
    <div css={css`
      width: 100%;
      margin: 0;
      padding-top: 0 !important;

      ${desktop(css`
        padding-bottom: ${largeSpacing}px !important;
      `)};

      ${notDesktop(css`
        padding-bottom: ${standardSpacing}px !important;
      `)};
      border-top: 1px solid ${gray5};
    `}
    />
  );

  const searchBarRef = useRef(null);
  const angularServices = useContext(AngularServicesContext);

  useEffect(() => {
    angularServices.$state.current.data = {
      ...angularServices.$state.current.data,
      ...{
        titleKey: 'INSTITUTIONS.LICENSES.LICENSE_AND_ENROLLMENT',
        headerClass: 'light-header fixed-header institution-l1-header',
        mainClass: 'advanced-settings-main license-enrollment-main',
      },
    };

    // Force reloading the header classes to force showing the Angular.js L1 header on this page
    angularServices.$scope.MainGridCtrl.getDataClasses();
    angularServices.$rootScope.displayTitleKey = angularServices.$state.current.data.titleKey;

    embedAngularTemplate('institutions/templates/license-enrollment-main.html', searchBarRef, angularServices);
  }, []);

  const match = useRouteMatch();

  const searchResultsRef = useRef(null);
  const SearchResults = () => {
    useEffect(() => {
      embedAngularTemplate('institutions/templates/org-users-search.html', searchResultsRef, angularServices);
    }, []);
    return <div ref={searchResultsRef} />;
  };

  return (
    <div className='license-enrollment'>
      <div ref={searchBarRef} />
      <NvRouterSwitch>
        <Route path={`${match.url}/dashboard`} exact>
          <div css={styles}>
            {/* Pass in the props dates here since we don't want to update the date range when the date selection is changed for this panel */}
            { props.licensePeriodDefined
              ? <LicenseUsagePanel startDate={props.periodStartDate} endDate={props.periodEndDate} /> : '' }
            <Border />
            <EnrollmentPanel setPeriod={setPeriod} />
            <TopEnrolledPanel startDate={startDate} endDate={endDate} periodType={periodType} dateText={periodText} />
          </div>
        </Route>
        <Route>
          <SearchResults />
        </Route>
      </NvRouterSwitch>
    </div>
  );
};

const ConnectedLicenseUsageDashboard = connect(
  (state: RootState): LicenseDashboardProps => ({
    licensePeriodDefined: !noContractPeriodDefined(state),
    periodStartDate: getPeriodStartDate(state),
    periodEndDate: getPeriodEndDate(state),
  }),
  {},
)(LicenseDashboardComponent);

export default ConnectedLicenseUsageDashboard;
